/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
  ProjectFormData,
  GeneratedCampaign,
  AdCopyVariation,
  User,
  UserPlan,
  ChatSession,
  BrandProfile,
  FooterLink,
  WorkspaceRoleContext,
} from './types';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { CommandPalette } from './components/CommandPalette';
import { StrategyResultsSkeleton } from './components/Skeletons';
import { ProjectForm } from './components/ProjectForm';
import { CampaignHistory } from './components/CampaignHistory';
import { MobileBottomNav } from './components/MobileBottomNav';
import { CreditConfirmationDialog } from './components/CreditConfirmationDialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Crown, Plus } from 'lucide-react';
// لوحة الأدمن (وTranslationManager وAdminSupportConsole المستوردين جواها) كود ضخم بيخص
// الأدمن/فريق الإدارة بس — lazy import هنا بدل static import عشان ميتحمّلش أصلاً ضمن
// الحزمة (bundle) الرئيسية لأي مستخدم عادي مش أدمن، غير وقت فتح تبويب "admin" فعليًا.
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then((module) => ({ default: module.AdminDashboard })));
import { api, setUnauthorizedHandler, notifyAuthenticated } from './utils/api';
import { useToast } from './contexts/ToastContext';
import { Logo } from './components/Logo';
import { useTranslation } from './i18n/translations';
import { WorkspaceDashboard } from './components/WorkspaceDashboard';
import { BrandProfileSettings, EMPTY_BRAND_PROFILE } from './components/BrandProfileSettings';
import {
  AppTab,
  navigate,
  pathForAdminSection,
  pathForCampaign,
  pathForChatSession,
  pathForTab,
  resolveRoute,
} from './utils/routes';
const MarketInsights = lazy(() => import('./components/MarketInsights').then((module) => ({ default: module.MarketInsights })));
const AIChat = lazy(() => import('./components/AIChat').then((module) => ({ default: module.AIChat })));
const CampaignWorkspace = lazy(() => import('./components/CampaignWorkspace').then((module) => ({ default: module.CampaignWorkspace })));
const TeamWorkspace = lazy(() => import('./components/TeamWorkspace').then((module) => ({ default: module.TeamWorkspace })));
const ContentLibrary = lazy(() => import('./components/ContentLibrary').then((module) => ({ default: module.ContentLibrary })));
const ProfileModal = lazy(() => import('./components/ProfileModal').then((module) => ({ default: module.ProfileModal })));
const AdPreviewModal = lazy(() => import('./components/AdPreviewModal').then((module) => ({ default: module.AdPreviewModal })));
const PricingModal = lazy(() => import('./components/PricingModal').then((module) => ({ default: module.PricingModal })));
const ReelsScriptModal = lazy(() => import('./components/ReelsScriptModal').then((module) => ({ default: module.ReelsScriptModal })));
const MultiPlatformModal = lazy(() => import('./components/MultiPlatformModal').then((module) => ({ default: module.MultiPlatformModal })));
const CompetitorAnalyzerModal = lazy(() => import('./components/CompetitorAnalyzerModal').then((module) => ({ default: module.CompetitorAnalyzerModal })));
const SupportChatWidget = lazy(() => import('./components/SupportChatWidget').then((module) => ({ default: module.SupportChatWidget })));
const AuthPage = lazy(() => import('./components/AuthPage').then((module) => ({ default: module.AuthPage })));
const ClientReviewPage = lazy(() => import('./components/ClientReviewPage').then((module) => ({ default: module.ClientReviewPage })));
const MarketingPage = lazy(() => import('./components/MarketingPage').then((module) => ({ default: module.MarketingPage })));
const LegalPage = lazy(() => import('./components/LegalPage').then((module) => ({ default: module.LegalPage })));
const ContactPage = lazy(() => import('./components/ContactPage').then((module) => ({ default: module.ContactPage })));
const FaqPage = lazy(() => import('./components/FaqPage').then((module) => ({ default: module.FaqPage })));
const AboutPage = lazy(() => import('./components/AboutPage').then((module) => ({ default: module.AboutPage })));
const DemoPage = lazy(() => import('./components/DemoPage').then((module) => ({ default: module.DemoPage })));
const BlogPage = lazy(() => import('./components/BlogPage').then((module) => ({ default: module.BlogPage })));
const BlogPostPage = lazy(() => import('./components/BlogPostPage').then((module) => ({ default: module.BlogPostPage })));
const NotFoundPage = lazy(() => import('./components/NotFoundPage').then((module) => ({ default: module.NotFoundPage })));

export default function App() {
  const { showToast } = useToast();
  const { t, tx, language } = useTranslation();
  const [routeLocation, setRouteLocation] = useState(() => ({ pathname: window.location.pathname, search: window.location.search }));
  const route = resolveRoute(routeLocation.pathname, routeLocation.search);
  const reviewToken = route.reviewToken || null;
  const TAB_TITLES: Record<AppTab, string> = {
    dashboard: t('tab.dashboard'),
    builder: t('tab.builder'),
    saved: t('tab.saved'),
    library: t('tab.library'),
    insights: t('tab.insights'),
    chat: t('tab.chat'),
    team: t('tab.team'),
    account: tx('الحساب والإعدادات'),
    admin: t('tab.admin'),
  };
  const TAB_DESCRIPTIONS: Record<AppTab, string> = {
    dashboard: tx('الرصيد، النشاط، وأهم إجراءات مساحة العمل في مكان واحد.'),
    builder: tx('أدخل تفاصيل العرض وابنِ استراتيجية حملة قابلة للتنفيذ.'),
    saved: tx('راجع حملاتك السابقة وافتح أي ملف لاستكمال العمل.'),
    library: tx('نظّم النصوص والزوايا الإعلانية القابلة لإعادة الاستخدام.'),
    insights: tx('اقرأ اتجاهات السوق وتقديرات تكلفة النتائج.'),
    chat: tx('اسأل مساعد استهداف عن الجمهور والميزانية والرسائل.'),
    team: tx('أدر أعضاء الفريق والتكليفات وحدود الاستخدام.'),
    account: tx('حدّث بياناتك وأمانك ورصيدك وأجهزة تسجيل الدخول.'),
    admin: tx('راقب المنصة وأدر المستخدمين والباقات والتشغيل.'),
  };
  const [activeTab, setActiveTab] = useState<AppTab>(() => route.tab || 'dashboard');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('istihdaf_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  // كثافة العرض: مضغوطة تلقائيًا داخل لوحة الأدمن (جداول أكتر على الشاشة)، ومريحة
  // في باقي التطبيق. مفيش زرار تبديل يدوي بعد الآن (كان مربكًا وبدون فايدة واضحة للمستخدم).
  const densityMode: 'comfortable' | 'compact' = activeTab === 'admin' ? 'compact' : 'comfortable';

  // روابط الفوتر — تُدار بالكامل من لوحة المالك (admin) وتُجلب من السيرفر بدل ما
  // تكون ثابتة في الكود، عشان المالك يقدر يضيف/يحذف/يعدّل أي رابط من غير تعديل الكود.
  const [footerLinks, setFooterLinks] = useState<FooterLink[]>([]);

  useEffect(() => {
    const refreshFooterLinks = () => api.getFooterLinks()
      .then((res) => { if (Array.isArray(res.links)) setFooterLinks(res.links); })
      .catch(() => {});
    refreshFooterLinks();
    window.addEventListener('istihdaf:footer-links-changed', refreshFooterLinks);
    return () => window.removeEventListener('istihdaf:footer-links-changed', refreshFooterLinks);
  }, []);

  useEffect(() => {
    const syncRoute = () => setRouteLocation({ pathname: window.location.pathname, search: window.location.search });
    window.addEventListener('popstate', syncRoute);
    window.addEventListener('istihdaf:navigation', syncRoute);
    return () => {
      window.removeEventListener('popstate', syncRoute);
      window.removeEventListener('istihdaf:navigation', syncRoute);
    };
  }, []);

  useEffect(() => {
    if (route.tab) setActiveTab(route.tab);
  }, [route.tab]);

  const navigateToTab = (tab: AppTab) => {
    setActiveTab(tab);
    navigate(pathForTab(tab));
  };

  useEffect(() => {
    try {
      localStorage.setItem('istihdaf_sidebar_collapsed', String(isSidebarCollapsed));
    } catch {
      // Device-local preference only; the interface still works when storage is unavailable.
    }
  }, [isSidebarCollapsed]);

  // Active User Session State (hydrated from the server via JWT, not localStorage)
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [workspaceRoleContext, setWorkspaceRoleContext] = useState<WorkspaceRoleContext | null>(null);
  const [isAuthOpen, setIsAuthOpen] = useState<boolean>(true);
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(true);

  // Admin-only: full users list, fetched on demand from the server
  const [users, setUsers] = useState<User[]>([]);

  const [currentCampaign, setCurrentCampaign] = useState<GeneratedCampaign | null>(null);
  const [savedCampaigns, setSavedCampaigns] = useState<GeneratedCampaign[]>([]);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [brandProfile, setBrandProfile] = useState<BrandProfile>(EMPTY_BRAND_PROFILE);

  // AI Chat sessions — lifted up so the Sidebar's "recent" list can show them
  // alongside saved campaigns instead of hiding them inside the chat page.
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [activeChatSessionId, setActiveChatSessionId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Preview Modal State
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewCopy, setPreviewCopy] = useState<AdCopyVariation | null>(null);
  const [previewBannerUrl, setPreviewBannerUrl] = useState<string | undefined>(undefined);

  // Pricing Modal State
  const [isPricingOpen, setIsPricingOpen] = useState<boolean>(false);

  useEffect(() => {
    if (currentUser?.mustChangePassword) navigate('/app/account');
  }, [currentUser?.mustChangePassword]);

  // New AI Feature Modal States
  const [isReelsOpen, setIsReelsOpen] = useState<boolean>(false);
  const [isMultiPlatformOpen, setIsMultiPlatformOpen] = useState<boolean>(false);
  const [isCompetitorOpen, setIsCompetitorOpen] = useState<boolean>(false);

  // Global Ctrl+K / Cmd+K shortcut to open the command palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // On first load, try to restore the session from the httpOnly session cookie
  // (set by the server on login; the browser attaches it automatically, so we just
  // ask the server "who am I" — if there's no valid cookie it responds 401).
  useEffect(() => {
    api
      .me({ silent: true })
      .then((res) => {
        setCurrentUser(res.user);
        setIsAuthOpen(false);
      })
      .catch(() => {
        setIsAuthOpen(true);
      })
      .finally(() => setIsBootstrapping(false));
  }, []);

  // معالج 401 عام: لو أي نداء API (غير نداءات نموذج تسجيل الدخول نفسه) رجع 401
  // في أي وقت أثناء الاستخدام — يعني الجلسة اتبطّلت من الخادم (تغيير كلمة مرور،
  // تسجيل خروج عن بُعد، انتهاء التوكن...إلخ) — نرجّع المستخدم فوراً لشاشة الدخول
  // بدل ما يفضل شايف لوحة (زي لوحة الأدمن) بيانات فاضية ونداءات فاشلة بصمت في
  // الـ console من غير أي تفسير له. مسجَّل مرة واحدة بس عند تحميل التطبيق.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setCurrentUser(null);
      setIsAuthOpen(true);
      showToast(tx('انتهت صلاحية جلسة الدخول، يرجى تسجيل الدخول مرة أخرى'), 'error');
    });
    return () => setUnauthorizedHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // بمجرد ما يبقى عندنا مستخدم مسجّل دخول (بعد /api/auth/me الأول أو بعد تسجيل
  // دخول جديد)، نصفّر فلاج تنبيه "الجلسة انتهت" عشان لو اتبطّلت تاني بعد كده،
  // التنبيه يقدر يشتغل من جديد بدل ما يفضل معطّل من مرة سابقة.
  useEffect(() => {
    if (currentUser) notifyAuthenticated();
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser) { setWorkspaceRoleContext(null); return; }
    api.getWorkspaceRole()
      .then((result) => setWorkspaceRoleContext(result.context || null))
      .catch(() => setWorkspaceRoleContext(null));
  }, [currentUser?.id]);

  // لو آخر تبويب محفوظ محلياً كان "لوحة التحكم" لكن تبيّن بعد تحميل الجلسة إن
  // المستخدم مش مالك ولا عضو فريق إدارة (اتشال، أو الجلسة انتهت)، نرجّعه لتبويب
  // "لوحة القيادة" العادي بدل ما يشوف شاشة فاضية.
  useEffect(() => {
    if (isBootstrapping || activeTab !== 'admin') return;
    const hasAdminAccess =
      !!currentUser && (currentUser.role === 'admin' || (currentUser.adminAccess?.permissions?.length ?? 0) > 0);
    if (!hasAdminAccess) {
      navigateToTab('dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBootstrapping, currentUser]);

  // Server-calculated in-account alerts use the percentage configured by the admin and
  // also cover trial reminders at 3 days and 1 day.
  const accountAlertShownRef = React.useRef('');
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    const checkAccountAlerts = () => api.getCreditSummary().then((result) => {
      if (cancelled) return;
      if (result.user && (result.user.credits !== currentUser.credits || result.user.plan !== currentUser.plan || result.user.trialStatus !== currentUser.trialStatus)) {
        setCurrentUser(result.user);
      }
      const notification = (result.summary?.notifications || []).find((item: any) => ['credits_exhausted', 'credits_low', 'trial_expiring'].includes(item.type));
      if (!notification) { accountAlertShownRef.current = ''; return; }
      const key = `${notification.type}:${notification.daysRemaining || ''}:${result.summary?.creditsRemaining}`;
      if (accountAlertShownRef.current === key) return;
      accountAlertShownRef.current = key;
      const message = notification.type === 'credits_exhausted'
        ? tx('انتهى رصيد الكريدات. اشحن أو جدّد باقتك للمتابعة.')
        : notification.type === 'credits_low'
          ? `${tx('رصيدك منخفض وقارب على الانتهاء.')} (${result.summary.creditsRemaining})`
          : `${tx('تنتهي فترتك التجريبية خلال')} ${notification.daysRemaining} ${tx('يوم')}.`;
      showToast(message, notification.level === 'danger' ? 'error' : 'info', { label: tx('فتح الحساب'), onClick: () => navigate('/app/account') });
    }).catch(() => {});
    void checkAccountAlerts();
    const timer = window.setInterval(checkAccountAlerts, 6 * 60 * 60 * 1000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [currentUser?.id, currentUser?.credits, currentUser?.plan, currentUser?.trialStatus]);

  // Load the current user's saved campaigns from the database whenever the session changes
  useEffect(() => {
    if (!currentUser) {
      setSavedCampaigns([]);
      setCampaignsLoaded(false);
      return;
    }
    setCampaignsLoaded(false);
    api
      .listCampaigns()
      .then((res) => setSavedCampaigns(res.campaigns))
      .catch((err) => console.error('Failed to load saved campaigns:', err))
      .finally(() => setCampaignsLoaded(true));
  }, [currentUser?.id]);

  useEffect(() => {
    if (!route.campaignId || !savedCampaigns.length) return;
    const selected = savedCampaigns.find((campaign) => campaign.id === route.campaignId);
    if (selected && selected.id !== currentCampaign?.id) setCurrentCampaign(selected);
  }, [route.campaignId, savedCampaigns, currentCampaign?.id]);

  useEffect(() => {
    if (!campaignsLoaded || !route.campaignId || currentCampaign?.id === route.campaignId) return;
    if (!savedCampaigns.some((campaign) => campaign.id === route.campaignId)) {
      showToast(tx('تعذر العثور على الحملة المطلوبة'), 'error');
      navigate('/app/campaigns', true);
    }
  }, [campaignsLoaded, route.campaignId, savedCampaigns, currentCampaign?.id]);

  useEffect(() => {
    if (!currentUser) {
      setBrandProfile(EMPTY_BRAND_PROFILE);
      return;
    }
    api.getBrandProfile()
      .then((result) => setBrandProfile(result.profile || EMPTY_BRAND_PROFILE))
      .catch(() => setBrandProfile(EMPTY_BRAND_PROFILE));
  }, [currentUser?.id]);

  // Load the current user's AI chat sessions whenever the session changes,
  // so they can appear in the Sidebar's recent list right away.
  useEffect(() => {
    if (!currentUser) {
      setChatSessions([]);
      setActiveChatSessionId(null);
      return;
    }
    api
      .listChatSessions()
      .then((res) => setChatSessions(res.sessions))
      .catch((err) => console.error('Failed to load chat sessions:', err));
  }, [currentUser?.id]);

  useEffect(() => {
    if (route.chatSessionId && route.chatSessionId !== activeChatSessionId) {
      setActiveChatSessionId(route.chatSessionId);
    }
  }, [route.chatSessionId, activeChatSessionId]);

  // Load the full users list for the admin dashboard whenever it's opened — for the
  // owner always, and for staff members who were granted the 'users' section specifically
  // (needed for both the Users tab table and the owner-only overview health panel).
  useEffect(() => {
    const canSeeUsers =
      currentUser?.role === 'admin' || (currentUser?.adminAccess?.permissions?.includes('users') ?? false);
    if (activeTab === 'admin' && canSeeUsers) {
      api
        .adminListUsers()
        .then((res) => setUsers(res.users))
        .catch((err) => console.error('Failed to load users list:', err));
    }
  }, [activeTab, currentUser?.role, currentUser?.adminAccess]);

  // Auth Functions
  const handleLoginSuccess = (user: User) => {
    setCurrentUser(user);
    setIsAuthOpen(false);
    if (route.kind === 'auth' || route.kind === 'marketing') navigate('/app');
  };

  const handleLogout = () => {
    api.logout().catch(() => {});
    setCurrentUser(null);
    setSavedCampaigns([]);
    setChatSessions([]);
    setActiveChatSessionId(null);
    setIsAuthOpen(true);
    navigate('/');
  };

  // Profile Modal: sync updated user data (name/avatar/email/dateOfBirth) back into session state
  const handleProfileUserUpdate = (updatedUser: User) => {
    setCurrentUser(updatedUser);
  };

  // Profile Modal: account was permanently deleted — close the modal and log out
  const handleAccountDeleted = () => {
    showToast(tx('تم حذف حسابك بنجاح'), 'info');
    handleLogout();
  };

  // Admin User Updates (Manual Plan Upgrades & Credit Changes)
  const handleUpdateUserByAdmin = async (updatedUser: User) => {
    try {
      const res = await api.adminUpdateUser(updatedUser.id, updatedUser);
      setUsers((prev) => prev.map((u) => (u.id === res.user.id ? res.user : u)));
      if (currentUser && currentUser.id === res.user.id) {
        setCurrentUser(res.user);
      }
    } catch (err: any) {
      showToast(err.message || tx('حدث خطأ أثناء تحديث بيانات المستخدم'), 'error');
    }
  };

  const handleAddUserByAdmin = async (newUserFields: Omit<User, 'id' | 'createdAt'>) => {
    try {
      const res = await api.adminAddUser(newUserFields);
      setUsers((prev) => [res.user, ...prev]);
      showToast(
        `${tx('تم إنشاء الحساب بنجاح.')} ${tx('البريد:')} ${res.user.email} · ${tx('كلمة المرور المؤقتة:')} ${res.tempPassword}`,
        'success'
      );
    } catch (err: any) {
      showToast(err.message || tx('حدث خطأ أثناء إنشاء المستخدم'), 'error');
    }
  };

  const handleDeleteUserByAdmin = async (userId: string) => {
    try {
      await api.adminDeleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err: any) {
      showToast(err.message || tx('حدث خطأ أثناء حذف المستخدم'), 'error');
    }
  };

  // NOTE: This is an admin-only instant plan switch used for testing/support, separate
  // from the real (manual Instapay/wallet transfer) payment flow used by regular users.
  const handleManualPlanChangeFromModal = async (newPlan: UserPlan) => {
    if (!currentUser) return;

    try {
      const planResponse = await api.getPaymentPlans();
      const newCredits = Number(planResponse.plans?.[newPlan]?.credits_granted ?? currentUser.credits);
      const res = await api.adminUpdateUser(currentUser.id, { plan: newPlan, credits: newCredits });
      setCurrentUser(res.user);
    } catch (err: any) {
      showToast(err.message || tx('حدث خطأ أثناء تحديث الباقة'), 'error');
    }
  };

  // Handle generating a sector-aware marketing campaign strategy.
  const handleGenerateStrategy = async (formData: ProjectFormData) => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }

    if (currentUser.credits <= 0) {
      setIsPricingOpen(true);
      return;
    }

    setIsLoading(true);

    try {
      const resData = await api.generateStrategy(formData);

      const newCampaign: GeneratedCampaign = {
        id: `camp_${Date.now()}`,
        createdAt: new Date().toISOString(),
        projectInfo: formData,
        strategy: resData.data.strategy,
        adCopies: resData.data.adCopies,
        status: 'draft',
      };

      setCurrentCampaign(newCampaign);
      try {
        await api.saveCampaign(newCampaign, { label: tx('مسودة مولّدة تلقائيًا'), changeSummary: tx('إنشاء الحملة وحفظها تلقائيًا') });
        setSavedCampaigns((current) => [newCampaign, ...current.filter((campaign) => campaign.id !== newCampaign.id)]);
        void api.updateOnboarding({ firstCampaignSaved: true });
        void api.trackProductEvent('campaign_saved', newCampaign.id, { source: 'auto_save' });
      } catch (saveError) {
        console.error('Auto-save generated campaign failed:', saveError);
        showToast(tx('تم إنشاء الحملة لكن تعذر حفظها تلقائيًا؛ استخدم زر الحفظ داخل الحملة.'), 'info');
      }
      void api.updateOnboarding({ firstCampaignCreated: true, projectName: formData.projectName });
      void api.trackProductEvent(savedCampaigns.length ? 'second_campaign_created' : 'first_campaign_created', newCampaign.id);

      // Credit was already deducted server-side; sync the fresh user record (credits, etc.)
      if (resData.user) setCurrentUser(resData.user);
      navigate(pathForCampaign(newCampaign.id));

    } catch (err: any) {
      // أخطاء الرصيد/الصلاحية/البيانات لا يجوز تجاوزها بمولد محلي.
      if (err?.status && err.status < 500) {
        if (err.status === 402) setIsPricingOpen(true);
        showToast(err.message || tx('تعذر إنشاء الحملة'), 'error');
        return;
      }

      console.error('API Error, preparing intelligent fallback generator:', err);
      const fallbackCampaign = generateFallbackCampaign(formData);

      // الـ fallback نفسه حملة كاملة، لذلك يُخصم له رصيد قبل إظهاره للمستخدم.
      try {
        const res = await api.consumeCredit();
        setCurrentUser(res.user);
      } catch (creditErr: any) {
        console.error('Failed to sync credit consumption:', creditErr);
        if (creditErr?.status === 402) setIsPricingOpen(true);
        showToast(creditErr?.message || tx('تعذر خصم رصيد الحملة'), 'error');
        return;
      }

      setCurrentCampaign(fallbackCampaign);
      try {
        await api.saveCampaign(fallbackCampaign, { label: tx('مسودة احتياطية مولّدة تلقائيًا'), changeSummary: tx('حفظ نتيجة المولد الاحتياطي تلقائيًا') });
        setSavedCampaigns((current) => [fallbackCampaign, ...current.filter((campaign) => campaign.id !== fallbackCampaign.id)]);
        void api.updateOnboarding({ firstCampaignSaved: true });
        void api.trackProductEvent('campaign_saved', fallbackCampaign.id, { source: 'auto_save_fallback' });
      } catch (saveError) {
        console.error('Auto-save fallback campaign failed:', saveError);
        showToast(tx('تم إنشاء الحملة لكن تعذر حفظها تلقائيًا؛ استخدم زر الحفظ داخل الحملة.'), 'info');
      }
      void api.updateOnboarding({ firstCampaignCreated: true, projectName: formData.projectName });
      void api.trackProductEvent(savedCampaigns.length ? 'second_campaign_created' : 'first_campaign_created', fallbackCampaign.id);
      navigate(pathForCampaign(fallbackCampaign.id));
    } finally {
      setIsLoading(false);
    }
  };

  // Save Campaign (persisted in the database, not localStorage)
  const handleSaveCampaign = async (campaignToSave: GeneratedCampaign) => {
    const exists = savedCampaigns.some((c) => c.id === campaignToSave.id);
    try {
      await api.saveCampaign(campaignToSave, { label: exists ? tx('حفظ تحديثات الحملة') : tx('الإصدار الأول'), changeSummary: exists ? tx('حفظ يدوي من مساحة العمل') : tx('إنشاء الحملة وحفظها') });
      setSavedCampaigns((current) => exists ? current.map((item) => item.id === campaignToSave.id ? campaignToSave : item) : [campaignToSave, ...current]);
      setCurrentCampaign(campaignToSave);
      await Promise.allSettled([api.updateOnboarding({ firstCampaignSaved: true }), api.trackProductEvent('campaign_saved', campaignToSave.id)]);
      showToast(tx('تم حفظ الحملة بنجاح'), 'success');
    } catch (err: any) {
      showToast(err.message || tx('حدث خطأ أثناء حفظ الحملة'), 'error');
    }
  };

  const handleUpdateCampaign = async (updatedCampaign: GeneratedCampaign, versionMeta?: { label?: string; variantKey?: 'main' | 'A' | 'B'; changeSummary?: string }) => {
    await api.saveCampaign(updatedCampaign, versionMeta);
    setCurrentCampaign(updatedCampaign);
    setSavedCampaigns((current) => {
      const exists = current.some((campaign) => campaign.id === updatedCampaign.id);
      return exists
        ? current.map((campaign) => campaign.id === updatedCampaign.id ? updatedCampaign : campaign)
        : [updatedCampaign, ...current];
    });
  };

  const handleRestoreCampaign = async (restoredCampaign: GeneratedCampaign) => {
    setCurrentCampaign(restoredCampaign);
    setSavedCampaigns((current) => current.map((campaign) => campaign.id === restoredCampaign.id ? restoredCampaign : campaign));
  };

  // Delete Saved Campaign
  const handleDeleteCampaign = async (id: string) => {
    try {
      await api.deleteCampaign(id);
      setSavedCampaigns(savedCampaigns.filter((c) => c.id !== id));
      showToast(tx('تم حذف الحملة'), 'info');
    } catch (err: any) {
      showToast(err.message || tx('حدث خطأ أثناء حذف الحملة'), 'error');
    }
  };

  // Open Preview Modal
  const handleOpenPreview = (adCopy: AdCopyVariation, bannerUrl?: string) => {
    setPreviewCopy(adCopy);
    setPreviewBannerUrl(bannerUrl);
    setIsPreviewOpen(true);
  };

  const isCurrentSaved = currentCampaign
    ? savedCampaigns.some((c) => c.id === currentCampaign.id)
    : false;

  if (reviewToken) {
    return <Suspense fallback={<div className="cms-loading">{tx('جاري التحميل...')}</div>}><ClientReviewPage token={reviewToken} /></Suspense>;
  }

  if (isBootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-page)' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 flex items-center justify-center animate-pulse">
            <Logo className="w-14 h-14" />
          </div>
          <div className="text-sm font-bold" style={{ color: 'var(--text-muted)' }}>{t('auth.loading')}</div>
        </div>
      </div>
    );
  }

  if (route.kind === 'marketing') {
    if (route.authMode === 'demo') {
      return <Suspense fallback={<div className="cms-loading">{tx('جاري التحميل...')}</div>}><DemoPage /></Suspense>;
    }
    if (route.marketingPage === 'privacy' || route.marketingPage === 'terms') {
      return <Suspense fallback={<div className="cms-loading">{tx('جاري التحميل...')}</div>}><LegalPage currentUser={currentUser} footerLinks={footerLinks} focus={route.marketingPage} /></Suspense>;
    }
    if (route.marketingPage === 'contact') {
      return <Suspense fallback={<div className="cms-loading">{tx('جاري التحميل...')}</div>}><ContactPage currentUser={currentUser} footerLinks={footerLinks} /></Suspense>;
    }
    if (route.marketingPage === 'faq') {
      return <Suspense fallback={<div className="cms-loading">{tx('جاري التحميل...')}</div>}><FaqPage currentUser={currentUser} footerLinks={footerLinks} /></Suspense>;
    }
    if (route.marketingPage === 'about') {
      return <Suspense fallback={<div className="cms-loading">{tx('جاري التحميل...')}</div>}><AboutPage currentUser={currentUser} footerLinks={footerLinks} /></Suspense>;
    }
    if (route.marketingPage === 'blog') {
      return <Suspense fallback={<div className="cms-loading">{tx('جاري التحميل...')}</div>}>{route.blogSlug || route.blogPreviewId ? <BlogPostPage slug={route.blogSlug} previewId={route.blogPreviewId} currentUser={currentUser} footerLinks={footerLinks} /> : <BlogPage currentUser={currentUser} footerLinks={footerLinks} />}</Suspense>;
    }
    return <Suspense fallback={<div className="cms-loading">{tx('جاري التحميل...')}</div>}><MarketingPage currentUser={currentUser} footerLinks={footerLinks} /></Suspense>;
  }

  if (route.kind === 'notFound') {
    return <Suspense fallback={null}><NotFoundPage /></Suspense>;
  }

  if (route.kind === 'auth' || isAuthOpen) {
    return <Suspense fallback={<div className="cms-loading">{tx('جاري التحميل...')}</div>}><AuthPage initialMode={route.authMode || 'login'} onLoginSuccess={handleLoginSuccess} onBackToWebsite={() => navigate('/')} onModeChange={(mode) => navigate(`/${mode}`)} /></Suspense>;
  }

  const goToCampaign = (camp: GeneratedCampaign) => {
    setCurrentCampaign(camp);
    setActiveTab('builder');
    navigate(pathForCampaign(camp.id));
  };

  const goToCampaignApproval = (camp: GeneratedCampaign) => {
    setCurrentCampaign(camp);
    setActiveTab('builder');
    navigate(pathForCampaign(camp.id, 'approval'));
  };

  const goToChatSession = (session: ChatSession) => {
    setActiveChatSessionId(session.id);
    setActiveTab('chat');
    navigate(pathForChatSession(session.id));
  };

  const startNewProject = () => {
    setCurrentCampaign(null);
    setActiveTab('builder');
    navigate('/app/campaigns/new');
    setTimeout(() => {
      const formEl = document.getElementById('project-form-anchor');
      if (formEl) formEl.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  };

  // نغلق إجراءات الإنشاء لحين وصول الدور من الخادم، حتى لا تومض صلاحيات المالك
  // أو بياناته المالية أمام عضو فريق أثناء تحميل سياق مساحة العمل.
  const canCreateCampaign = !!workspaceRoleContext && (
    workspaceRoleContext.isWorkspaceOwner
    || workspaceRoleContext.permissions.includes('create_campaigns')
  );
  const canDeleteCampaign = !!workspaceRoleContext && (
    workspaceRoleContext.isWorkspaceOwner
    || workspaceRoleContext.permissions.includes('delete_campaigns')
  );

  return (
    <ErrorBoundary sectionName="app-root" fallbackMessage="حصلت مشكلة غير متوقعة في التطبيق.">
    <div className={`app-root density-${densityMode} min-h-screen font-sans antialiased selection:bg-amber-300 selection:text-slate-950`}>

      {/* Fixed Sidebar (ChatGPT-style) */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={navigateToTab}
        currentUser={currentUser}
        savedCampaigns={savedCampaigns}
        currentCampaignId={currentCampaign?.id}
        onSelectCampaign={goToCampaign}
        chatSessions={chatSessions}
        activeChatSessionId={activeChatSessionId}
        onSelectChatSession={goToChatSession}
        onNewProject={startNewProject}
        onOpenPricing={() => setIsPricingOpen(true)}
        onLogout={handleLogout}
        onOpenCommandPalette={() => setIsCommandPaletteOpen(true)}
        onOpenProfile={() => navigateToTab('account')}
        isMobileOpen={isMobileSidebarOpen}
        onCloseMobile={() => setIsMobileSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapsed={() => setIsSidebarCollapsed((value) => !value)}
        canCreateCampaign={canCreateCampaign}
        workspaceRole={workspaceRoleContext?.role}
      />

      {/* Command Palette (Ctrl+K) */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        setActiveTab={navigateToTab}
        onNewProject={startNewProject}
        onOpenPricing={() => setIsPricingOpen(true)}
        onLogout={handleLogout}
        currentUser={currentUser}
        savedCampaigns={savedCampaigns}
        onSelectCampaign={goToCampaign}
      />

      {/* Content column, offset for the fixed sidebar */}
      <div className={`app-content-column ${isSidebarCollapsed ? 'is-sidebar-collapsed' : ''} flex flex-col min-h-screen`}>
        <TopBar
          title={TAB_TITLES[activeTab]}
          description={TAB_DESCRIPTIONS[activeTab]}
          onOpenMobileSidebar={() => setIsMobileSidebarOpen(true)}
          primaryAction={activeTab === 'account'
            ? { label: tx('ترقية الباقة'), onClick: () => setIsPricingOpen(true), tone: 'upgrade', icon: <Crown className="w-4 h-4" /> }
            : ['dashboard', 'saved', 'library'].includes(activeTab) && canCreateCampaign
              ? { label: tx('حملة جديدة'), onClick: startNewProject, icon: <Plus className="w-4 h-4" /> }
              : undefined}
        />

        <main className={`app-workspace flex-1 w-full mx-auto px-4 sm:px-6 xl:px-8 py-5 sm:py-7 ${activeTab === 'chat' ? 'max-w-[1320px]' : 'max-w-[1480px]'}`}>
          {/* key={activeTab} بيخلي الـ ErrorBoundary يتصفّر تلقائيًا لما ننتقل لقسم تاني،
              فلو قسم معيّن فيه مشكلة، القسم ده بس اللي هيتأثر وهيرجع يشتغل عادي بمجرد
              الانتقال بعيد عنه ورجوع تاني — بدل ما التطبيق كله يوقّع على شاشة بيضاء. */}
          <ErrorBoundary key={`${activeTab}-${route.campaignId || ''}-${route.workspaceStage || ''}-${route.adminSection || ''}`} sectionName={activeTab}>

          {activeTab === 'dashboard' && currentUser && (
            <WorkspaceDashboard
              user={currentUser}
              campaigns={savedCampaigns}
              roleContext={workspaceRoleContext}
              onNewProject={startNewProject}
              onOpenCampaign={goToCampaign}
              onOpenApproval={goToCampaignApproval}
              onOpenSaved={() => navigateToTab('saved')}
              onOpenInsights={() => navigateToTab('insights')}
              onOpenChat={() => navigateToTab('chat')}
              onOpenLibrary={() => navigateToTab('library')}
              onOpenPricing={() => setIsPricingOpen(true)}
            />
          )}

          {/* Campaign builder: the brief and the campaign workspace now have separate URLs. */}
          {activeTab === 'builder' && !route.campaignId && (
            <div className="space-y-6">
              <section className="builder-intro">
                <span>{t('hero.badge')}</span>
                <h2>{t('hero.heading')}</h2>
                <p>{t('hero.subtitle')}</p>
              </section>

              <BrandProfileSettings profile={brandProfile} onChange={setBrandProfile} />

              <div id="project-form-anchor">
                <ProjectForm
                  onSubmit={handleGenerateStrategy}
                  isLoading={isLoading}
                  remainingCredits={currentUser?.credits || 0}
                  currentPlan={currentUser?.plan || 'free'}
                  brandProfile={brandProfile}
                />
              </div>
              {isLoading && <StrategyResultsSkeleton />}
            </div>
          )}

          {activeTab === 'builder' && route.campaignId && currentCampaign?.id !== route.campaignId && (
            <div className="module-loading"><span className="spinner-soft" /> {tx('جاري تحميل ملف الحملة...')}</div>
          )}

          {activeTab === 'builder' && route.campaignId && currentCampaign?.id === route.campaignId && (
            <Suspense fallback={<div className="module-loading"><span className="spinner-soft" /> {tx('جاري تحميل مساحة الحملة...')}</div>}>
              <CampaignWorkspace
                campaign={currentCampaign}
                brandProfile={brandProfile}
                activeStage={route.workspaceStage || 'overview'}
                onStageChange={(stage) => navigate(pathForCampaign(currentCampaign.id, stage))}
                onUpdateCampaign={handleUpdateCampaign}
                onRestoreCampaign={handleRestoreCampaign}
                currentPlan={currentUser?.plan || 'free'}
                onCreditUsed={setCurrentUser}
                onUpgradeNeeded={() => setIsPricingOpen(true)}
                onSaveCampaign={handleSaveCampaign}
                isSaved={isCurrentSaved}
                onOpenPreview={handleOpenPreview}
                onOpenReels={() => setIsReelsOpen(true)}
                onOpenMultiPlatform={() => setIsMultiPlatformOpen(true)}
                onOpenCompetitor={() => setIsCompetitorOpen(true)}
              />
            </Suspense>
          )}

          {/* Tab 2: Saved Campaigns */}
          {activeTab === 'saved' && (
            <CampaignHistory
              savedCampaigns={savedCampaigns}
              onSelectCampaign={goToCampaign}
              onDeleteCampaign={handleDeleteCampaign}
              onNewCampaign={startNewProject}
              canCreateCampaign={canCreateCampaign}
              canDeleteCampaign={canDeleteCampaign}
            />
          )}

          {activeTab === 'library' && currentUser && (
            <Suspense fallback={<div className="module-loading"><span className="spinner-soft" /> {tx('جاري تحميل المكتبة...')}</div>}><ContentLibrary campaigns={savedCampaigns} /></Suspense>
          )}

          {/* Tab 3: Market Insights */}
          {activeTab === 'insights' && (
            <Suspense fallback={<div className="module-loading"><span className="spinner-soft" /></div>}><MarketInsights
              currentPlan={currentUser?.plan || 'free'}
              onUpgradeNeeded={() => setIsPricingOpen(true)}
              campaigns={savedCampaigns.filter((campaign) => (campaign.projectInfo.industry || 'real_estate') === 'real_estate')}
              onCreditUsed={setCurrentUser}
            /></Suspense>
          )}

          {/* Tab: AI Chat */}
          {activeTab === 'chat' && (
            <Suspense fallback={<div className="module-loading"><span className="spinner-soft" /></div>}><AIChat
              sessions={chatSessions}
              activeSessionId={activeChatSessionId}
              onSessionsChange={setChatSessions}
              onActiveSessionChange={setActiveChatSessionId}
              onUserUpdate={setCurrentUser}
            /></Suspense>
          )}

          {activeTab === 'team' && currentUser && (
            <Suspense fallback={<div className="module-loading"><span className="spinner-soft" /></div>}><TeamWorkspace currentPlan={currentUser.plan} campaigns={savedCampaigns} onUpgradeNeeded={() => setIsPricingOpen(true)} currentUserEmail={currentUser.email} /></Suspense>
          )}

          {activeTab === 'account' && currentUser && (
            <Suspense fallback={<div className="module-loading"><span className="spinner-soft" /></div>}><ProfileModal
              isOpen
              variant="page"
              onClose={() => navigateToTab('dashboard')}
              currentUser={currentUser}
              onUserUpdate={handleProfileUserUpdate}
              onAccountDeleted={handleAccountDeleted}
            /></Suspense>
          )}

          {/* Tab 4: Owner Admin Dashboard (owner كامل، أو عضو فريق إدارة عنده صلاحيات جزئية) */}
          {activeTab === 'admin' && currentUser && (currentUser.role === 'admin' || (currentUser.adminAccess?.permissions?.length ?? 0) > 0) && (
            <Suspense fallback={<div className="module-loading"><span className="spinner-soft" /> {tx('جاري تحميل لوحة التحكم...')}</div>}>
              <AdminDashboard
                users={users}
                onUpdateUser={handleUpdateUserByAdmin}
                onAddUser={handleAddUserByAdmin}
                onDeleteUser={handleDeleteUserByAdmin}
                totalCampaignsCount={savedCampaigns.length + (currentCampaign ? 1 : 0)}
                adminAccess={currentUser.adminAccess || { isOwner: currentUser.role === 'admin', permissions: [] }}
                initialSection={route.adminSection || 'overview'}
                onSectionChange={(section) => navigate(pathForAdminSection(section))}
              />
            </Suspense>
          )}
          </ErrorBoundary>
        </main>

        {/* Footer */}
        <footer className="app-footer py-5 text-center text-[10px] mt-4">
          <div className="max-w-[1480px] mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Logo className="w-5 h-5" />
              <span className="font-semibold">{tx('استهداف')}</span>
            </div>
            {footerLinks.length > 0 && (
              <nav className="flex items-center gap-4 flex-wrap justify-center">
                {footerLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    className="font-medium hover:underline"
                    {...(link.open_in_new_tab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {language === 'en' ? link.label_en : link.label_ar}
                  </a>
                ))}
              </nav>
            )}
            <p className="font-medium">© 2026 {t('footer.rights')}.</p>
          </div>
        </footer>
      </div>

      <MobileBottomNav
        activeTab={activeTab}
        onNavigate={(tab) => tab === 'builder' && canCreateCampaign ? startNewProject() : navigateToTab(tab)}
        onMore={() => setIsMobileSidebarOpen(true)}
        canCreateCampaign={canCreateCampaign}
        workspaceRole={workspaceRoleContext?.role}
      />

      <CreditConfirmationDialog />

      {/* Live Meta Ad Preview Modal */}
      {previewCopy && currentCampaign && (
        <Suspense fallback={null}><AdPreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          adCopy={previewCopy}
          projectInfo={currentCampaign.projectInfo}
          bannerUrl={previewBannerUrl}
        /></Suspense>
      )}

      {/* SaaS Pricing Modal */}
      {isPricingOpen&&<Suspense fallback={null}><PricingModal
        isOpen={isPricingOpen}
        onClose={() => setIsPricingOpen(false)}
        currentUser={currentUser}
        onSelectPlan={handleManualPlanChangeFromModal}
        onOpenAdminPanel={() => navigateToTab('admin')}
        onPlanUpgraded={async () => {
          // إعادة تحميل بيانات المستخدم بعد نجاح الدفع
          try {
            const updated = await api.me();
            if (updated?.user) setCurrentUser(updated.user);
          } catch { /* silent */ }
        }}
      /></Suspense>}

      {/* Reels/TikTok Script Generator Modal */}
      {currentCampaign && isReelsOpen && (
        <Suspense fallback={null}><ReelsScriptModal
          isOpen={isReelsOpen}
          onClose={() => setIsReelsOpen(false)}
          projectData={currentCampaign.projectInfo}
          onCreditUsed={setCurrentUser}
          currentPlan={currentUser?.plan || 'free'}
        /></Suspense>
      )}

      {/* Multi-Platform Ads Modal */}
      {currentCampaign && isMultiPlatformOpen && (
        <Suspense fallback={null}><MultiPlatformModal
          isOpen={isMultiPlatformOpen}
          onClose={() => setIsMultiPlatformOpen(false)}
          projectData={currentCampaign.projectInfo}
          onCreditUsed={setCurrentUser}
        /></Suspense>
      )}

      {/* Competitor Ad Analyzer Modal */}
      {isCompetitorOpen&&<Suspense fallback={null}><CompetitorAnalyzerModal
        isOpen={isCompetitorOpen}
        onClose={() => setIsCompetitorOpen(false)}
        projectContext={currentCampaign ? `${currentCampaign.projectInfo.projectName} في ${currentCampaign.projectInfo.region}` : undefined}
        onCreditUsed={setCurrentUser}
      /></Suspense>}

      {/* ودجة الدعم الفني العائمة: تظهر فقط للمستخدمين العاديين (الأدمن عنده لوحة إدارة مخصصة) */}
      {currentUser && currentUser.role !== 'admin' && <Suspense fallback={null}><SupportChatWidget /></Suspense>}

    </div>
    </ErrorBoundary>
  );
}

// Intelligent Fallback Generator for seamless runtime reliability
function generateFallbackCampaign(formData: ProjectFormData): GeneratedCampaign {
  if ((formData.industry || 'real_estate') !== 'real_estate') {
    return generateGeneralFallbackCampaign(formData);
  }
  const isGulf = formData.audienceType === 'gulf_expat';

  return {
    id: `camp_${Date.now()}`,
    createdAt: new Date().toISOString(),
    projectInfo: formData,
    strategy: {
      targetLocation: {
        primaryRegions: [
          formData.region,
          isGulf ? 'الرياض - جدة - دبي - الكويت' : 'القاهرة الجديدة - الشيخ زايد - المعادي',
        ],
        radiusOrPins: 'دائرة 15 كم من الموقع المباشر + استهداف المغتربين',
        descriptionAr: `استهداف جغرافيا مركز في ${formData.region} بالإضافة إلى شرائح القوة الشرائية المرتفعة.`,
      },
      demographics: {
        ageRange: isGulf ? '32 - 60 سنة' : '28 - 55 سنة',
        gender: 'رجال ونساء',
        incomeBracketAr: 'أعلى 10% إلى 25% من حيث القدرة الشرائية في مصر والخليج',
      },
      interests: [
        {
          category: 'الاستثمار والتطوير العقاري',
          englishKeywords: ['Real estate investment', 'Luxury property', 'Property management'],
          arabicExplanation: 'المهتمين بعوائد الاستثمار والأصول المرتفعة والقيمة.',
        },
        {
          category: 'الماركات والأسلوب الملاحي الفاخر',
          englishKeywords: ['Luxury goods', 'Frequent international travelers', 'Golf clubs'],
          arabicExplanation: 'شرائح تمتلك السيولة والملاءة المالية للشراء المباشر.',
        },
      ],
      behaviors: {
        englishBehaviors: ['Frequent travelers', 'iOS users (iPhone 14/15/16)', 'High-value goods buyers'],
        arabicExplanation: 'سلوكيات حاملي أجهزة أبل الحديثة والمسافرين دائماً للخارج.',
      },
      exclusions: {
        englishKeywords: ['Job hunting', 'Free courses', 'Cheap apartments'],
        arabicReason: 'استبعاد الباحثين عن الوظائف أو الخيارات الاقتصادية جداً لتجنب الليد الفاسد.',
      },
      campaignSetup: {
        objective: 'LEAD_GENERATION',
        recommendedDailyBudget: '800 - 1,500 EGP / يومياً',
        estimatedCplRange: '80 - 160 EGP / ليد مجمع',
        placements: ['Facebook Feed', 'Instagram Feed', 'Instagram Stories', 'Reels'],
        housingCategoryNotice: 'تم تفعيل فئة الإسكان الخاصة (Special Ad Category: Housing) لتفادي رفض الحملة.',
      },
    },
    adCopies: [
      {
        id: 'investment',
        titleAr: 'الزاوية الاستثمارية - العائد الذهبي',
        angleDescription: 'تركّز على العائد الاستثماري المرتفع، خفض المخاطرة، وأطول فترة سداد.',
        hook: `ليه تسيب فلوسك في البنك لما تفكيرك الاستثماري يقدر يحققلك أعلى عائد في ${formData.region}؟`,
        primaryText: `فرصة استثمارية حقيقية بمشروع ${formData.projectName} من المطور ${formData.developerName || 'الرائد'}.\n\nامتلك ${formData.unitType} بأسعار تبدأ من ${formData.startingPrice.toLocaleString()} ${formData.currency} فقط!\n\n✔️ مقدم يبدأ من ${formData.downPaymentPercent}%\n✔️ تقسيط مريح على ${formData.installmentYears} سنوات بدون فوائد\n✔️ استلام ${formData.deliveryYear}\n\nاحجز استشارتك المباشرة وسجل بياناتك لمعرفة جدول الأقساط والعوائد المتوقعة!`,
        headline: `احجز ${formData.unitType} بمقدم ${formData.downPaymentPercent}% فقط في ${formData.region}`,
        description: `تقسيط على ${formData.installmentYears} سنوات | مشروع ${formData.projectName}`,
        callToAction: 'تسجيل البيانات',
        visualPromptIdea: `صورة معمارية فائقة الجودة لـ ${formData.unitType} حديثة في ${formData.region} بإضاءة غروب شمس دافئة وأجواء فاخرة جداً.`,
        keyHighlights: ['عائد استثماري مرتفع', `تقسيط ${formData.installmentYears} سنوات`, 'موقع استراتيجي'],
      },
      {
        id: 'lifestyle',
        titleAr: 'الزاوية المعيشية - أسلوب الحياة الرفيع',
        angleDescription: 'تخاطب العائلة والراحة النفسية والرفاهية والخدمات الحصرية.',
        hook: `حياتك وحياة عيلتك تستاهل مستوى تاني خالص من الهدوء والرفاهية...`,
        primaryText: `في كمبوند ${formData.projectName} بـ ${formData.region}، هتصحى كل يوم على مناظر طبيعية وخدمات متكاملة.\n\nغير أسلوب حياتك وابدأ حياة جديدة في ${formData.unitType} بأسعار تبدأ من ${formData.startingPrice.toLocaleString()} ${formData.currency}.\n\nأطول فترة سداد تصل إلى ${formData.installmentYears} سنوات ومقدم ${formData.downPaymentPercent}% فقط.\n\nاضغط على التواصل الآن لمعرفة المخطط والأسعار التفصيلية!`,
        headline: `عش الرفاهية بـ ${formData.region} | كمبوند ${formData.projectName}`,
        description: `استلام ${formData.deliveryYear} - أقساط مريحة ومساحات متنوعة`,
        callToAction: 'إرسال رسالة',
        visualPromptIdea: `منظر لـ ${formData.unitType} محاطة بمسطحات خضراء وحمام سباحة زجاجي وأطفال يلعبون بأمان.`,
        keyHighlights: ['خدمات متكاملة', 'هدوء وأمان 24/7', 'تصميمات عالمية'],
      },
      {
        id: 'urgency',
        titleAr: 'زاوية الندرة والسرعة - الطرح الحالي',
        angleDescription: 'تعتمد على الحصرية والعدد المحدود من الوحدات المتاحة للفرصة الذهبية.',
        hook: `الفرصة دي مش بتتكرر كتير في ${formData.region}... عدد وحدات محدود بأسعار المرحلة الأولى!`,
        primaryText: `قبل زيادة الأسعار المرتقبة، سجل اهتمامك الآن واحجز ${formData.unitType} في ${formData.projectName}.\n\nمقدم يبدأ من ${formData.downPaymentPercent}% وبسعر تبدأ من ${formData.startingPrice.toLocaleString()} ${formData.currency} وتقسيط على ${formData.installmentYears} سنة.\n\nبادر بالتسجيل الآن قبل نفاذ المرحلة الأولى!`,
        headline: `فرصة الطرح الأول بـ ${formData.region} - سجل بياناتك قبل غلق المبيعات`,
        description: `أحدث مشاريع ${formData.developerName || 'المطور'} - حجز مباشر`,
        callToAction: 'حجز الآن',
        visualPromptIdea: `تصميم جرافيك حديث وعصري مكتوب عليه بخط فاخر: مرحلة أولى بـ ${formData.region} - خصم الحجز المبكر.`,
        keyHighlights: ['أسعار المرحلة الأولى', 'أقساط متساوية', 'خصم الحجز المبكر'],
      },
    ],
  };
}

function generateGeneralFallbackCampaign(formData: ProjectFormData): GeneratedCampaign {
  const profiles: Record<Exclude<ProjectFormData['industry'], 'real_estate'>, { interests: string[]; cpl: string; policy: string }> = {
    ecommerce: {
      interests: ['Online shopping', 'E-commerce', formData.offerType || 'Consumer products'],
      cpl: '60 - 220 EGP / نتيجة تقديرية',
      policy: 'راجع دقة السعر والتوفر وسياسة الاسترجاع وتطابق الإعلان مع صفحة المنتج قبل النشر.',
    },
    professional_services: {
      interests: ['Entrepreneurship', 'Small business', formData.offerType || 'Business services'],
      cpl: '150 - 500 EGP / ليد تقديري',
      policy: 'تجنب ضمان النتائج، ووضّح نطاق الخدمة وشروطها وبيانات مقدمها في الإعلان وصفحة الهبوط.',
    },
    education: {
      interests: ['Online education', 'Professional development', formData.offerType || 'Training'],
      cpl: '100 - 350 EGP / ليد تقديري',
      policy: 'لا تقدّم وعودًا مضمونة بالنجاح أو التوظيف، ووضّح محتوى البرنامج ومدته وشروط التسجيل.',
    },
    healthcare: {
      interests: ['Health care', 'Wellness', formData.offerType || 'Medical clinic'],
      cpl: '180 - 550 EGP / حجز تقديري',
      policy: 'تجنب الإيحاء بمعرفة حالة صحية شخصية أو الوعد بنتيجة علاجية، وراجع موافقات المحتوى الطبي والخصوصية.',
    },
    restaurants: {
      interests: ['Restaurants', 'Food delivery', formData.offerType || 'Food and drink'],
      cpl: '25 - 100 EGP / طلب أو حجز تقديري',
      policy: 'اعرض السعر والعرض ومدة صلاحيته ونطاق التوصيل بوضوح، وتأكد من تطابق الصور مع المنتج الفعلي.',
    },
    automotive: {
      interests: ['Automobiles', 'Car dealership', formData.offerType || 'Vehicle services'],
      cpl: '220 - 700 EGP / ليد تقديري',
      policy: 'وضّح حالة السيارة والسعر وشروط التمويل أو الضمان، وتجنب أي أسعار أو توافر غير مؤكد.',
    },
    tourism: {
      interests: ['Travel', 'Hotels', formData.offerType || 'Tourism'],
      cpl: '180 - 650 EGP / حجز أو ليد تقديري',
      policy: 'وضّح التواريخ وما يشمله السعر وسياسة الإلغاء والتوفر، ولا تستخدم أسعاراً أو صوراً مضللة.',
    },
    fitness: {
      interests: ['Physical fitness', 'Gyms', formData.offerType || 'Fitness'],
      cpl: '90 - 350 EGP / اشتراك أو ليد تقديري',
      policy: 'تجنب الإشارة إلى صفات جسدية شخصية أو ضمان نتائج، ووضّح شروط الاشتراك ومستوى البرنامج.',
    },
    mobile_apps: {
      interests: ['Mobile apps', 'Software', formData.offerType || 'Digital services'],
      cpl: '70 - 400 EGP / تسجيل أو اشتراك تقديري',
      policy: 'وضّح السعر والاشتراك والخصوصية وما يقدمه التطبيق فعلياً، وتجنب ادعاءات الأداء غير المثبتة.',
    },
  };
  const industry = (formData.industry || 'professional_services') as Exclude<ProjectFormData['industry'], 'real_estate'>;
  const profile = profiles[industry] || profiles.professional_services;
  const price = formData.startingPrice > 0 ? `${formData.startingPrice.toLocaleString()} ${formData.currency}` : 'سعر حسب التفاصيل';
  const brand = formData.developerName || 'فريقنا';
  const benefitList = formData.keyFeatures.length ? formData.keyFeatures : ['حل واضح لاحتياج العميل', 'تجربة بسيطة ومباشرة', 'دعم قبل وبعد الطلب'];
  const promotion = formData.promotionDetails?.trim() || 'تواصل معنا لمعرفة التفاصيل';
  const cta = formData.conversionChannel === 'website' ? 'اطلب الآن' : formData.conversionChannel === 'store' ? 'زر الفرع' : formData.conversionChannel === 'calls' ? 'اتصل الآن' : 'تواصل الآن';
  const objective = formData.campaignGoal === 'sales'
    ? 'SALES'
    : formData.campaignGoal === 'website_traffic'
      ? 'TRAFFIC'
      : formData.campaignGoal === 'brand_awareness'
        ? 'AWARENESS'
        : formData.campaignGoal === 'messages'
          ? 'MESSAGES_WHATSAPP'
          : formData.campaignGoal === 'bookings' && formData.conversionChannel === 'website'
            ? 'CONVERSIONS'
            : formData.campaignGoal === 'store_visits'
              ? 'TRAFFIC'
            : 'LEAD_GENERATION';

  return {
    id: `camp_${Date.now()}`,
    createdAt: new Date().toISOString(),
    projectInfo: formData,
    strategy: {
      targetLocation: {
        primaryRegions: [formData.region, formData.subArea || formData.region].filter((value, index, values) => value && values.indexOf(value) === index),
        radiusOrPins: formData.subArea ? `اختبار نطاق ${formData.subArea} ومقارنته باستهداف ${formData.region}` : `ابدأ بالسوق المحدد في ${formData.region}`,
        descriptionAr: `ابدأ بجمهور داخل ${formData.region} ثم قارن جمهورًا واسعًا بجمهور مرتبط باهتمامات المجال.` ,
      },
      demographics: {
        ageRange: '23 - 55 سنة كبداية اختبار',
        gender: 'رجال ونساء حسب ملاءمة العرض',
        incomeBracketAr: formData.targetAudience || 'يُختبر حسب سعر العرض وسلوك العملاء الفعلي',
      },
      interests: [
        {
          category: 'اهتمامات مرتبطة بالمجال',
          englishKeywords: profile.interests,
          arabicExplanation: 'ابدأ بهذه الإشارات كاختبار، وقارنها بجمهور واسع بدل الاعتماد عليها وحدها.',
        },
        {
          category: 'نية الشراء والمشكلة',
          englishKeywords: ['Engaged shoppers', 'Digital marketing'],
          arabicExplanation: formData.customerPainPoint || 'اختبر رسالة تركز على المشكلة مقابل رسالة تركز على النتيجة.',
        },
      ],
      behaviors: {
        englishBehaviors: ['Website visitors', 'Social media engagers'],
        arabicExplanation: 'استخدم جماهيرك المخصصة للزوار والمتفاعلين والعملاء السابقين متى كانت متاحة وبموافقة سليمة.',
      },
      exclusions: {
        englishKeywords: ['Existing customers', 'Job hunting'],
        arabicReason: 'افصل العملاء الحاليين والباحثين عن وظائف عندما لا يخدمون هدف الحملة، واستخدم بياناتك المملوكة فقط.',
      },
      campaignSetup: {
        objective,
        recommendedDailyBudget: `${Math.max(150, Math.round((formData.monthlyBudgetEGP || 30000) / 30)).toLocaleString()} EGP / يومياً كبداية`,
        estimatedCplRange: profile.cpl,
        placements: ['Facebook Feed', 'Instagram Feed', 'Instagram Stories', 'Reels'],
        housingCategoryNotice: profile.policy,
      },
    },
    adCopies: [
      {
        id: 'investment',
        titleAr: 'زاوية القيمة والنتيجة',
        angleDescription: 'تشرح ما الذي سيحصل عليه العميل ولماذا يستحق العرض التكلفة.',
        hook: `${formData.customerPainPoint ? `${formData.customerPainPoint}؟` : 'بتدور على حل عملي وواضح؟'} ${formData.projectName} معمول عشان يوصلك للنتيجة بخطوات أبسط.`,
        primaryText: `${brand} بيقدم لك ${formData.projectName}: ${formData.unitType}.\n\n${benefitList.map((item) => `✓ ${item}`).join('\n')}\n\nالسعر: ${price}. ${promotion}.\n\n${cta} واعرف التفاصيل المناسبة لك.`,
        headline: `${formData.projectName} — قيمة واضحة بسعر ${price}`,
        description: benefitList.slice(0, 2).join(' · '),
        callToAction: cta,
        visualPromptIdea: `تصميم إعلاني واقعي ونظيف لعرض ${formData.projectName} يوضح المنتج أو الخدمة والنتيجة الأساسية بدون ادعاءات مبالغ فيها.`,
        keyHighlights: benefitList.slice(0, 3),
      },
      {
        id: 'lifestyle',
        titleAr: 'زاوية المشكلة وتجربة العميل',
        angleDescription: 'تربط العرض بموقف يومي حقيقي وتوضح سهولة التجربة.',
        hook: `لو محتاج ${formData.projectName} من غير تعقيد، خلّي التجربة تبدأ من احتياجك الحقيقي.`,
        primaryText: `${formData.unitType}\n\nمع ${brand} هتلاقي تجربة واضحة تبدأ من فهم احتياجك وتنتهي بخطوة عملية. ${benefitList.join('، ')}.\n\n${promotion} — ${cta}.`,
        headline: `حل أبسط مع ${formData.projectName}`,
        description: `متاح للجمهور في ${formData.region}`,
        callToAction: cta,
        visualPromptIdea: `مشهد واقعي لعميل يستخدم ${formData.projectName} في سياقه اليومي مع هوية ${brand} البصرية وإضاءة طبيعية.`,
        keyHighlights: benefitList.slice(0, 3),
      },
      {
        id: 'urgency',
        titleAr: 'زاوية العرض والإجراء',
        angleDescription: 'تعرض سببًا واضحًا للتحرك الآن اعتمادًا على تفاصيل العرض الحقيقية فقط.',
        hook: promotion !== 'تواصل معنا لمعرفة التفاصيل' ? promotion : `ابدأ خطوتك مع ${formData.projectName} النهارده.`,
        primaryText: `${promotion}.\n\nاحصل على ${formData.projectName} من ${brand} بسعر ${price}. ${formData.unitType}\n\n${cta} وخد كل التفاصيل قبل اتخاذ القرار.`,
        headline: `${promotion} | ${formData.projectName}`,
        description: `عرض ${brand} في ${formData.region}`,
        callToAction: cta,
        visualPromptIdea: `تصميم مباشر وواضح يبرز عرض ${formData.projectName} والسعر والدعوة للإجراء بدون خلق ندرة غير حقيقية.`,
        keyHighlights: [promotion, ...benefitList].slice(0, 3),
      },
    ],
  };
}
